import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';

const PaginationMePos = ({
                           totalPages = 1,
                           currentPage = 0,
                           onPageChange = () => {},
                           firstItemIndex = null,
                           lastItemIndex = null,
                           totalItems = null,
                           maxPagesToShow = 5,
                         }) => {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.floor(totalPages));
  const current = Math.max(0, Math.min(currentPage, pages - 1));

  const getPageLabel = (pageIndex) => pageIndex + 1;

  // Generating a range of pages to display
  const getVisiblePages = () => {
    if (pages <= maxPagesToShow) {
      // Showing all pages
      return Array.from({ length: pages }, (_, i) => i);
    }

    // Define a range around the current page
    let start = Math.max(0, current - Math.floor(maxPagesToShow / 2));
    let end = start + maxPagesToShow - 1;

    // We will make adjustments if we go beyond the limits.
    if (end >= pages) {
      end = pages - 1;
      start = Math.max(0, end - maxPagesToShow + 1);
    } else if (start < 0) {
      start = 0;
      end = Math.min(pages - 1, start + maxPagesToShow - 1);
    }

    const visiblePages = [];

    // Always show the first page
    if (start > 0) {
      visiblePages.push(0);
      if (start > 1) visiblePages.push('ellipsis-start');
    }

    // Main range
    for (let i = start; i <= end; i++) {
      visiblePages.push(i);
    }

    // Always show the last page
    if (end < pages - 1) {
      if (end < pages - 2) visiblePages.push('ellipsis-end');
      visiblePages.push(pages - 1);
    }

    return visiblePages;
  };

  const visiblePages = getVisiblePages();

  const PageButton = ({ pageIndex, isActive = false }) => (
    <li className={`page-item ${isActive ? 'active' : ''}`}>
      <Button
        className="page-link"
        onClick={() => !isActive && onPageChange(pageIndex)}
      >
        {getPageLabel(pageIndex)}
      </Button>
    </li>
  );

  const Ellipsis = () => (
    <li className="page-item disabled">
      <span className="page-link">
        <i className="bi bi-three-dots text-dark" />
      </span>
    </li>
  );

  const showItemRange = typeof firstItemIndex === 'number' &&
    typeof lastItemIndex === 'number' &&
    typeof totalItems === 'number';

  return (
    <div className="me-pos-pagination d-flex justify-content-between align-items-center">
      <div></div>

      <nav>
        <ul className="d-flex gap-3 pagination mb-0">
          {/* Back button */}
          <li className={`page-item ${current === 0 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => current > 0 && onPageChange(current - 1)}
              disabled={current === 0}
            >
              <i className="bi bi-chevron-left page-link-arrow" />
            </button>
          </li>

          {/* Pages */}
          {visiblePages.map((page, index) => {
            if (page === 'ellipsis-start') {
              return <Ellipsis key="ellipsis-start" />;
            }
            if (page === 'ellipsis-end') {
              return <Ellipsis key="ellipsis-end" />;
            }
            return (
              <PageButton
                key={page}
                pageIndex={page}
                isActive={page === current}
              />
            );
          })}

          {/* Forward button */}
          <li className={`page-item ${current === pages - 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => current < pages - 1 && onPageChange(current + 1)}
              disabled={current === pages - 1}
            >
              <i className="bi bi-chevron-right page-link-arrow" />
            </button>
          </li>
        </ul>
      </nav>

      {showItemRange && (
        <div className="ms-3 text-info">
          {firstItemIndex} - {lastItemIndex} {t('salesStatistics.of')} {totalItems}
        </div>
      )}
    </div>
  );
};

export default PaginationMePos;